import type { DataReferenceOffer, DataReferenceOffers, DataReferenceSet } from '../api/client'
import { insertAtCaret } from './insertAtCaret'

/** The portion of an @ mention that is complete enough to choose a level. */
export interface Mention {
  start: number
  segments: string[]
  filter: string
}

/** One selectable row in the set, key, or field cascade. */
export type MentionRow =
  | { kind: 'level'; set: DataReferenceSet }
  | { kind: 'row'; set: DataReferenceSet; key: string }
  | { kind: 'field'; set: DataReferenceSet; key: string; field: string; offer: DataReferenceOffer }

export type MentionKeyActionResult =
  | { kind: 'move'; highlight: number }
  | { kind: 'pick' }
  | { kind: 'close' }
  | { kind: 'swallow' }

export type MentionKeyActionInput = {
  count: number
  highlight: number
}

/** Find the mention whose text ends at the caret. */
export function mentionAtCaret(text: string, caret: number): Mention | null {
  if (caret < 0 || caret > text.length) return null

  let start = caret
  while (start > 0 && !/\s/.test(text[start - 1])) start -= 1
  if (start >= caret || text[start] !== '@') return null

  const parts = text.slice(start + 1, caret).split('.')
  const filter = parts.pop()!
  if (parts.length > 2 || parts.some((segment) => segment.length === 0)) return null

  return { start, segments: parts, filter }
}

/** Return the stable identity shared by the popup key and the host's row-list key. */
export function mentionRowKey(row: MentionRow): string {
  if (row.kind === 'level') return `level:${row.set.set}`
  if (row.kind === 'row') return `row:${row.set.set}:${row.key}`
  return `field:${row.set.set}:${row.key}:${row.field}`
}

/** Project the loaded offers into the rows for the level named by a mention. */
export function mentionLevel(offers: DataReferenceOffers, mention: Mention): MentionRow[] {
  const filter = mention.filter.toLocaleLowerCase()

  if (mention.segments.length === 0) {
    return offers.sets
      .filter((set) => set.offers.length > 0)
      .filter((set) => set.set.toLocaleLowerCase().includes(filter))
      .map((set) => ({ kind: 'level', set }))
  }

  const set = offers.sets.find((candidate) => candidate.set === mention.segments[0])
  if (!set) return []

  if (mention.segments.length === 1) {
    return set.keys
      .filter((key) => key.toLocaleLowerCase().includes(filter))
      .map((key) => ({ kind: 'row', set, key }))
  }

  const key = mention.segments[1]
  if (!set.keys.includes(key)) return []

  const fieldOffers = set.offers.flatMap((offer) => {
    const path = offer.path.split('.')
    if (path.length !== 3 || path[0] !== 'by_key' || path[1] !== key) return []
    const field = path[2]
    if (!field.toLocaleLowerCase().includes(filter)) return []
    return [{ kind: 'field' as const, set, key, field, offer }]
  })
  const exactMatches = fieldOffers.filter((row) => row.field.toLocaleLowerCase() === filter)
  const otherMatches = fieldOffers.filter((row) => row.field.toLocaleLowerCase() !== filter)
  return [...exactMatches, ...otherMatches]
}

/** Map a composer key to the action the mention host should own. */
export function mentionKeyAction(
  key: string,
  { count, highlight }: MentionKeyActionInput,
): MentionKeyActionResult | null {
  if (key === 'Escape') return { kind: 'close' }

  if (key === 'Enter') return count > 0 ? { kind: 'pick' } : { kind: 'swallow' }

  if (key === 'ArrowDown' && count > 0) {
    return { kind: 'move', highlight: (highlight + 1) % count }
  }
  if (key === 'ArrowUp' && count > 0) {
    return { kind: 'move', highlight: (highlight - 1 + count) % count }
  }

  return null
}

function mentionEnd(value: string, caret: number, mention: Mention): number {
  let end = Math.max(mention.start, Math.min(caret, value.length))
  while (end < value.length && !/\s/.test(value[end])) end += 1
  return end
}

function rewriteMention(
  value: string,
  caret: number,
  mention: Mention,
  replacement: string,
): { value: string; caret: number } {
  const end = mentionEnd(value, caret, mention)
  const head = value.slice(0, mention.start)
  const tail = value.slice(end)
  return { value: head + replacement + tail, caret: head.length + replacement.length }
}

/** Replace the mention span with a selected level or field. */
export function applyMentionPick(
  value: string,
  caret: number,
  mention: Mention,
  pick: MentionRow,
): { value: string; caret: number } {
  if (pick.kind === 'level') {
    return rewriteMention(value, caret, mention, `@${pick.set.set}.`)
  }

  if (pick.kind === 'row') {
    return rewriteMention(value, caret, mention, `@${pick.set.set}.${pick.key}.`)
  }

  const replacement = `${pick.offer.token} (${pick.set.set})`
  return insertAtCaret(value, replacement, mention.start, mentionEnd(value, caret, mention))
}
